import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLine, Eyebrow } from "./Reveal";

const PILLARS = [
  ["Men", "Built for the blokes who carry it all and say nothing."],
  ["Tell", "Speaking up isn't weakness. It's the bravest thing you'll do."],
  ["Health", "Mind first. Because a stronger tomorrow starts with today."],
];

export const Mission = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const clip = useTransform(scrollYProgress, [0, 0.45], ["inset(18% 18% 18% 18% round 24px)", "inset(0% 0% 0% 0% round 24px)"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="story" ref={ref} data-testid="mission-section" className="relative bg-[#07090D] py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6 lg:sticky lg:top-32 self-start">
          <Eyebrow>01 — Why we exist</Eyebrow>
          <h2 className="mt-6 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
            <MaskLine inView>Men don't</MaskLine>
            <MaskLine inView delay={0.1}>always say it.</MaskLine>
            <MaskLine inView delay={0.2}><span className="text-[var(--mth-blue)]">So we wear it.</span></MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-base sm:text-lg text-slate-300 leading-relaxed">
              MenTellHealth started with one simple idea: if men won't open up, give them something that opens the door for them. Every hoodie is a conversation starter — on the train, at the gym, at the pub — and a quiet reminder that nobody has to carry it alone.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {PILLARS.map(([k, v], i) => (
              <Reveal key={k} delay={0.1 * i}>
                <div data-testid={`mission-pillar-${k.toLowerCase()}`} className="group bg-[#0B0F15] p-6 flex gap-6 items-baseline hover:bg-[#0F1520] transition-colors">
                  <span className={`font-display font-black text-3xl uppercase w-28 shrink-0 ${k === "Tell" ? "text-[var(--mth-blue)]" : ""}`}>{k}</span>
                  <span className="text-slate-300">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <motion.div style={{ clipPath: clip }} className="relative aspect-[3/4] overflow-hidden bg-[#0B0F15]">
            <motion.img style={{ y: imgY }} src="/images/portrait.jpg" alt="Model wearing the black MenTellHealth hoodie in a forest" className="absolute inset-0 h-[116%] w-full object-cover -top-[8%]" />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/85 to-transparent">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-200">Real men. Real conversations. Brighter tomorrows.</p>
            </div>
          </motion.div>
          <Reveal className="mt-10 rounded-2xl border border-white/10 p-8 bg-[#0B0F15]">
            <p className="font-display font-black text-6xl sm:text-7xl text-[var(--mth-blue)]">3 in 4</p>
            <p className="mt-3 text-slate-300 max-w-sm">suicides registered in the UK are men. That's why the conversation matters.</p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-slate-400">Source: Office for National Statistics</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
